<script setup lang="ts">
import { ref, computed } from 'vue';
import { type Modification, modificationLabel } from '../order-management';
const props = defineProps<{
  role: 'admin' | 'provider';
  orderId: string;
  paymentProvider?: string | null;
  modification: Modification;
  disabled?: boolean;
}>();
const emit = defineEmits<{ changed: [] }>();
const feasible = ref(false);
const reason = ref(''),
  notDelivered = ref(false),
  notConsumed = ref(false),
  busy = ref(false),
  error = ref('');
const controlAvailable = computed(() =>
  props.modification.paymentBeforeControl
    ? props.modification.controlAvailable === true
    : props.modification.status === 'REQUESTED',
);
const providerCanReject = computed(
  () =>
    props.role === 'provider' &&
    [
      'REQUESTED',
      'CONTROL_FAILED',
      'CHECKED',
      ...(props.modification.paymentBeforeControl ? ['PAYMENT_PENDING'] : []),
    ].includes(props.modification.status),
);
const pending = computed(() => !['APPLIED', 'REJECTED'].includes(props.modification.status));
const money = (v: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v / 100);
async function submit(decision?: 'APPROVE' | 'REJECT'): Promise<void> {
  if (busy.value || props.disabled) return;
  if (!reason.value.trim()) {
    error.value = 'Ajoutez une note de contrôle ou de décision.';
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    const action = props.role === 'provider' ? 'modification-check' : 'modification-decision';
    const response = await globalThis.fetch(
      `/${props.role}/api/delivery-requests/${encodeURIComponent(props.orderId)}/${action}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          modificationId: props.modification.id,
          reason: reason.value,
          ...(props.role === 'provider'
            ? {
                notDelivered: notDelivered.value,
                notConsumed: notConsumed.value,
                feasible: decision === 'REJECT' ? false : feasible.value,
                ...(decision ? { decision } : {}),
              }
            : { decision }),
        }),
      },
    );
    if (!response.ok)
      throw new Error(
        'Modification non validée. Actualisez et vérifiez le contrôle préparateur.',
      );
    emit('changed');
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <section class="modification" aria-label="Modification client">
    <h3>Modification client</h3>
    <p>{{ modification.from.displayName }} → {{ modification.to.displayName }}</p>
    <strong>{{
      role === 'provider' &&
      !modification.paymentBeforeControl &&
      modification.status === 'CHECKED'
        ? 'Contrôle enregistré · règlement en attente'
        : modificationLabel(modification)
    }}</strong>
    <p v-if="modification.reason">{{ modification.reason }}</p>
    <template v-if="role === 'admin' && modification.deltaMinor !== undefined">
      <p v-if="modification.deltaMinor > 0">
        Complément client : {{ money(modification.deltaMinor) }}.
        <template v-if="!modification.paymentBeforeControl">
          La préparation reprend après son paiement.
        </template>
        <template v-else-if="modification.paymentRequired">
          À régler avant le contrôle de faisabilité.
        </template>
      </p>
      <p v-else-if="modification.deltaMinor < 0">
        <template v-if="modification.status === 'REJECTED'">
          Réduction non retenue : {{ money(-modification.deltaMinor) }}. Aucun remboursement à
          valider pour cette modification. Offre initiale conservée.
        </template>
        <template v-else-if="modification.status === 'APPLIED'">
          Réduction appliquée : {{ money(-modification.deltaMinor) }}.
        </template>
        <template v-else>Réduction demandée : {{ money(-modification.deltaMinor) }}.</template>
      </p>
      <p v-else>Aucun écart de prix.</p>
      <p v-if="modification.paymentBeforeControl && modification.deltaMinor > 0">
        <template v-if="modification.paymentStatus === 'PAID'">
          Complément prélevé : {{ money(modification.capturedAmountMinor ?? 0) }}.<template
            v-if="(modification.refundedAmountMinor ?? 0) > 0"
          >
            Remboursé : {{ money(modification.refundedAmountMinor!) }}.
          </template>
        </template>
        <template
          v-else-if="['CANCELED', 'EXPIRED'].includes(modification.paymentStatus ?? '')"
        >
          Empreinte libérée ou expirée · aucun prélèvement de ce complément.
        </template>
      </p>
      <p
        v-if="
          modification.settlement &&
          (!modification.paymentBeforeControl || modification.deltaMinor < 0)
        "
      >
        {{
          paymentProvider === 'stripe'
            ? modification.settlement.kind === 'SUPPLEMENT_AUTHORIZED'
              ? 'Empreinte enregistrée :'
              : modification.settlement.kind === 'REDUCTION'
                ? 'Réduction validée :'
                : 'Règlement enregistré :'
            : 'Règlement simulé enregistré :'
        }}
        {{ money(modification.settlement.amountMinor) }}.
      </p>
    </template>
    <p
      v-if="role === 'admin' && modification.status === 'REQUESTED'"
      class="next-step"
      role="status"
    >
      En attente du contrôle préparateur : il doit vérifier la non-livraison, la
      non-consommation et la faisabilité avant de poursuivre.
    </p>
    <p
      v-if="role === 'admin' && modification.status === 'CONTROL_FAILED'"
      class="next-step"
      role="status"
    >
      Le contrôle préparateur est défavorable. Cette modification ne peut pas être validée.
    </p>
    <p
      v-if="
        role === 'admin' &&
        !modification.paymentBeforeControl &&
        modification.status === 'CHECKED' &&
        (modification.deltaMinor ?? 0) > 0
      "
      class="next-step"
      role="status"
    >
      Contrôle préparateur favorable. Le voyageur doit maintenant payer
      {{ money(modification.deltaMinor ?? 0) }} depuis « Annuler / modifier une commande ». La
      modification sera confirmée après paiement.
    </p>
    <p
      v-if="role === 'admin' && modification.status === 'PAYMENT_PENDING'"
      class="next-step"
      role="status"
    >
      Le paiement complémentaire du voyageur est en attente. La préparation reste suspendue.
    </p>
    <p v-if="modification.paymentBeforeControl && modification.paymentRequired" role="status">
      Le voyageur doit régler le complément avant le contrôle de faisabilité. La préparation
      reste suspendue.
    </p>
    <p
      v-if="
        modification.captureMethod === 'manual' && modification.paymentStatus === 'AUTHORIZED'
      "
    >
      Le complément est réservé sur la carte. Il sera prélevé à la confirmation de livraison si
      la modification est réalisable.
    </p>
    <p v-if="modification.withdrawalRequested">
      Le client souhaite abandonner cette modification. Contrôlez la non-livraison et la
      non-consommation avant la décision administrateur.
    </p>
    <fieldset :disabled="busy || disabled || modification.bankOperationFailed">
      <template v-if="role === 'provider' && (controlAvailable || providerCanReject)">
        <p>
          La préparation et la livraison sont suspendues. Vérifiez physiquement les deux
          conditions.
        </p>
        <label><input v-model="notDelivered" type="checkbox" /> Box non livrée</label>
        <label><input v-model="notConsumed" type="checkbox" /> Contenu non consommé</label>
        <label
          ><input v-model="feasible" type="checkbox" /> La modification est réalisable :
          produits et délai vérifiés</label
        >
        <p>
          Confirmez la faisabilité ou refusez la modification après les deux contrôles
          physiques.
        </p>
      </template>
      <template
        v-if="
          (role === 'provider' && (controlAvailable || providerCanReject)) ||
          (role === 'admin' && pending)
        "
      >
        <label>Note<textarea v-model="reason" maxlength="128" rows="2" /></label>
        <template v-if="role === 'provider'">
          <p>
            Si la modification est impossible, conservez la box initiale et contactez le
            voyageur pour lui expliquer la solution proposée.
          </p>
          <button v-if="controlAvailable" class="provider-save" type="button" @click="submit()">
            Enregistrer le contrôle de modification
          </button>
          <button
            v-if="providerCanReject"
            class="provider-refuse"
            type="button"
            :disabled="!notDelivered || !notConsumed"
            @click="submit('REJECT')"
          >
            Refuser la modification · conserver la box standard
          </button>
          <p v-if="modification.paymentStatus === 'PAID'">
            Le complément a déjà été encaissé : son remboursement nécessite la validation
            administrateur avant la reprise.
          </p>
        </template>
        <template v-else>
          <button
            v-if="(modification.deltaMinor ?? 0) < 0"
            type="button"
            :disabled="
              !['CHECKED', 'REFUND_PENDING', 'REFUND_FAILED'].includes(modification.status) &&
              !modification.bankOperationPending
            "
            @click="submit('APPROVE')"
          >
            {{
              modification.bankOperationPending
                ? 'Vérifier le traitement bancaire'
                : modification.paymentBeforeControl
                  ? 'Valider la réduction et son règlement'
                  : modification.status === 'REFUND_PENDING'
                    ? 'Vérifier le remboursement'
                    : modification.status === 'REFUND_FAILED'
                      ? 'Réessayer le remboursement'
                      : paymentProvider === 'stripe'
                        ? 'Autoriser le remboursement de la différence'
                        : 'Valider la modification et le remboursement simulé'
            }}
          </button>
          <button
            v-if="
              !['REFUND_PENDING', 'REFUND_FAILED', 'PAYMENT_PENDING'].includes(
                modification.status,
              )
            "
            type="button"
            @click="submit('REJECT')"
          >
            Refuser la modification · conserver la commande initiale
          </button>
        </template>
      </template>
    </fieldset>
    <p v-if="role === 'provider' && modification.status === 'REJECTED'">
      Commande initiale conservée. Contactez le voyageur avant de préparer la box standard.
    </p>
    <p v-if="error" role="alert">{{ error }}</p>
  </section>
</template>
<style scoped>
.next-step {
  background: #fff7ed;
  border-left: 4px solid #ea580c;
  padding: 0.75rem;
  overflow-wrap: anywhere;
}
.modification {
  padding: 1rem;
  margin: 1rem 0;
  background: #fffbeb;
  color: #172033;
  border: 2px solid #eab308;
  border-radius: 1rem;
  overflow-wrap: anywhere;
}
fieldset {
  padding: 0;
  border: 0;
  min-width: 0;
}
label {
  display: block;
  margin: 0.8rem 0;
}
textarea {
  display: block;
  width: 100%;
  box-sizing: border-box;
  background: white;
  color: #172033;
  padding: 0.7rem;
  border: 1px solid #64748b;
  border-radius: 0.5rem;
}
button {
  width: 100%;
  min-height: 44px;
  padding: 0.8rem;
  margin: 0.4rem 0;
  border: 0;
  border-radius: 0.5rem;
  background: #20214f;
  color: white;
}
button.provider-save {
  background: #166534;
}
button.provider-refuse {
  background: #b91c1c;
}
button:disabled {
  opacity: 0.5;
}
</style>
